import {
    AcceptanceCriteriaCollection,
    BaseZenputCollection,
    BaseZenputModel,
    Ingredient,
    Ingredients,
    Paginator
} from '../../../../backbone-unorganized/shared/forms';
import { MobileBaseField } from '../BaseField/models';
import { AcceptanceValue, ACCEPTANCE_CRITERIA_OPERATORS, CONDITION_OPERATORS_AND_INSTRUCTIONS, NA } from '../constants';
import { MobileTemperatureField, playAcceptanceValueSound } from '../temperatureField/models';
import { SensorTemperature, SensorTemperatures } from '../sensorTemperatureField/models';
import { hideLoadingDialog } from '../../../../shared/utils/notifications';

/**
    A temperature reading of 0 is a valid answer, only null/undefined/'' are empty;
*/
export const isFilledTemperatureValue = (value) => value != null && value !== '';

const TEMPERATURE_PROFILE_TYPE_HOLDING = 2;

const get_instruction_params = (condition_value, temp_unit) => {
    if (Array.isArray(condition_value)) {
        const min_temp_value =
            temp_unit === 'C'
                ? $f.utils.fahrenheit_to_centigrade(condition_value[0], $f.utils.DECIMALS_IN_TEMPERATURE_VALUE)
                : condition_value[0];
        const max_temp_value =
            temp_unit === 'C'
                ? $f.utils.fahrenheit_to_centigrade(condition_value[1], $f.utils.DECIMALS_IN_TEMPERATURE_VALUE)
                : condition_value[1];

        return { min_temp_value, max_temp_value, temp_unit };
    }

    return {
        temp_value:
            temp_unit === 'C'
                ? $f.utils.fahrenheit_to_centigrade(condition_value, $f.utils.DECIMALS_IN_TEMPERATURE_VALUE)
                : condition_value,
        temp_unit
    };
};

export const FollowUpActionField = MobileBaseField.extend({
    default: {
        id: null,
        title: '',
        value: null,
        required: false
    },

    initialize: function (attrs, options) {
        options = options || {};
        $.extend(this, options);

        FollowUpActionField.__super__.initialize.call(this, attrs, options);
    },

    validation: {
        value: function (value) {
            if (this.get('required') && value !== true) {
                return _t('mobile_message_error_invalid_ingredient_temperature');
            }
        }
    },

    is_dependent_field: function () {
        return true;
    },

    has_value: function () {
        return this.get('value') === true;
    }
});

export const FollowUpActionFields = BaseZenputCollection.extend({
    model: FollowUpActionField,
    required: false,

    get_num_errors: function () {
        return this.filter((field) => !field.isValid(true)).length;
    },

    is_valid: function () {
        return this.get_num_errors() === 0;
    },

    validate: function () {
        return;
    }
});

export const BaseIngredientTemperatureField = MobileBaseField.extend({
    getFieldAcceptanceValue: function (ingredient, collection) {
        const retemps = collection.where({ retemp_original_id: ingredient.id });
        if (retemps.length === 0) {
            return ingredient.get('acceptance_value');
        }
        const lastTry = retemps[retemps.length - 1];
        return lastTry.get('acceptance_value');
    },

    setAcceptanceValue: function (collection) {
        if (!collection) {
            return;
        }
        const originalFields = collection.where({ retemp_attempt_number: undefined });
        const fieldsAcceptanceValues = originalFields.map((field) => this.getFieldAcceptanceValue(field, collection));
        const areAllNeutral = fieldsAcceptanceValues.every((acceptanceValue) => !acceptanceValue);
        if (areAllNeutral) {
            this.set('acceptance_value', 0);
            return;
        }

        const acceptanceValue = fieldsAcceptanceValues.find((val) => val === AcceptanceValue.FAIL);
        this.set('acceptance_value', acceptanceValue === undefined ? AcceptanceValue.PASS : AcceptanceValue.FAIL);
    }
});

export const MobileIngredientTemperatureField = BaseIngredientTemperatureField.extend({
    temperature_collection: null, // IngredientTemperatures - values in model form
    ingredient_temperature_category_id: null, // Ingredient Category Id
    ingredient_temperature_id: null, // Ingredient Id - this is for single product
    ingredients_catalog: null, // All ingredients the user has access to
    location_id: null,
    _empty_location_selected: false,

    initialize: function (attrs, options) {
        this.ingredient_temperature_id = this.get_metadata_attribute('ingredient_temperature_data').ingredient_id;
        this.ingredient_temperature_category_id =
            this.get_metadata_attribute('ingredient_temperature_data').ingredient_category_id;
        this.temperature_collection = new IngredientTemperatures(null, {
            parent_field_id: this.get('id'),
            required: this.get('required'),
            ingredients_catalog: null,
            allow_na: this.get_metadata_attribute('allow_na'),
            random_check: this.get_metadata_attribute('ingredient_temperature_data').random_check,
            check_all: this.get_metadata_attribute('ingredient_temperature_data').check_all,
            form_collection: this.collection
        });

        MobileIngredientTemperatureField.__super__.initialize.apply(this, arguments);
        this.listenTo(this.temperature_collection, 'change add remove', () => {
            this.setAcceptanceValue(this.temperature_collection);
        });

        this.listenTo(this.temperature_collection, 'follow_up_actions_on_change', () => {
            this.validate();
        });
    },

    save_value: function () {
        this.set('value', this.temperature_collection.toJSON());
    },

    has_value: function () {
        return this.has_all_values();
    },

    get_num_invalid: function () {
        if (this.temperature_collection.get('follow_up_actions') !== null) {
            return this.temperature_collection.get_num_invalid();
        }

        return !this.is_valid ? 1 : 0;
    },

    change_ingredient_location: function (ingredient_location, location_id, silent) {
        if (this.location_id === location_id) {
            return;
        }

        if (!silent) {
            this.set('value', []);
        }
        this.location_id = location_id;
        let ingredients_in_location = null;

        if (this.ingredient_temperature_id) {
            ingredients_in_location = new MobileIngredients(
                ingredient_location.by_location_and_category(location_id, null).get(this.ingredient_temperature_id)
            );
        } else {
            ingredients_in_location = ingredient_location.by_location_and_category(
                location_id,
                this.ingredient_temperature_category_id
            );
        }
        this._empty_location_selected = this._is_location_without_ingredients(ingredients_in_location);
        this.temperature_collection.set_ingredients_catalog(ingredients_in_location, silent);
    },

    _is_location_without_ingredients: function (ingredients_in_location) {
        return ingredients_in_location.length === 0;
    },

    get_submission_value: function () {
        const ingredient_temperature_values = this.get('value');
        if (!ingredient_temperature_values || ingredient_temperature_values.length === 0) {
            return [];
        }

        // filter out empty values for submission
        return this._clean_up_values(ingredient_temperature_values);
    },

    _clean_up_follow_up_actions: function (follow_up_actions_raw) {
        return follow_up_actions_raw.map(({ id, title, value }) => ({ id, title, value: value || false }));
    },

    _clean_up_values: function (ingredient_temperature_values) {
        const clean_up_reducer = (filled_values, ingredient_temperature_value) => {
            const {
                id,
                name,
                required,
                retemp_attempt,
                value,
                acceptance_value,
                follow_up_actions: follow_up_actions_raw,
                reading_method
            } = ingredient_temperature_value;
            const follow_up_actions = follow_up_actions_raw
                ? this._clean_up_follow_up_actions(follow_up_actions_raw)
                : null;

            if (isFilledTemperatureValue(value)) {
                filled_values.push({
                    id,
                    name,
                    required,
                    retemp_attempt,
                    value,
                    acceptance_value,
                    follow_up_actions,
                    reading_method
                });
            }

            return filled_values;
        };

        return ingredient_temperature_values.reduce(clean_up_reducer, []);
    },

    has_all_values: function () {
        const empty_ingredient_temps = this.temperature_collection.filter(
            (ingredient_temp) => !isFilledTemperatureValue(ingredient_temp.get('value'))
        );

        return this.temperature_collection.length > 0 && empty_ingredient_temps.length === 0;
    },

    validation: {
        value: {
            fn: function (val, attr, computed) {
                // Set as valid when this field is required but there're no ingredients in the catalog
                if (this.get('required') && this.location_id && this._empty_location_selected) {
                    return;
                }
                if (
                    (this.get('required') && this.get('value').length === 0) ||
                    !this.temperature_collection.is_valid()
                ) {
                    return _t('mobile_message_error_invalid_ingredient_temperature');
                }
                return;
            }
        }
    },

    get_temperature_field_child_models: function () {
        const fields = [];
        const {
            temperature_collection: { models: temperature_models }
        } = this;

        if (temperature_models) {
            const temperature_fields = temperature_models.reduce(
                (child_models, model) => {
                    if (model.get('follow_up_actions')) {
                        const follow_up_action_models = model.get('follow_up_actions').models;
                        child_models.push(...follow_up_action_models);
                        return child_models;
                    }
                    return child_models;
                },
                [...temperature_models]
            );
            fields.push(...temperature_fields);
        }

        return fields;
    },

    // don't copy the value for media fields on autofill copy
    get_attributes_for_autofill: function () {
        return {};
    }
});

export const MobileEquipmentTemperatureField = BaseIngredientTemperatureField.extend({
    temperature_collection: null, // EquipmentTemperatures - values in model form
    equipment_collection: null, // EquipmentCollection - list of Equipment
    sensor_collection: null, // SensorTemperatures - list of sensors
    equipment_category_id: null, // Category id of equipment
    location_id: null,
    _location_has_equipment: true,

    initialize: function (attrs, options) {
        this.equipment_category_id = this.get_metadata_attribute('equipment_temperature_data').equipment_category_id;
        this.equipment_collection = new EquipmentCollection([], {
            equipment_category_id: this.equipment_category_id
        });
        this.temperature_collection = new EquipmentTemperatures(null, {
            parent_field_id: this.get('id'),
            equipment_collection: this.equipment_collection,
            required: this.get('required'),
            allow_na: this.get_metadata_attribute('allow_na'),
            form_collection: this.collection
        });
        this.sensor_collection = new SensorTemperatures();

        MobileEquipmentTemperatureField.__super__.initialize.apply(this, arguments);
        this.listenTo(this.temperature_collection, 'change add remove', () => {
            this.setAcceptanceValue(this.temperature_collection);
        });

        this.listenTo(this.temperature_collection, 'follow_up_actions_on_change', this.validate);
    },

    save_value: function () {
        this.set('value', this.temperature_collection.toJSON());
    },

    has_value: function () {
        return this.has_all_values();
    },

    get_num_invalid: function () {
        if (this.temperature_collection.get('follow_up_actions') !== null) {
            return this.temperature_collection.get_num_invalid();
        }

        return !this.is_valid ? 1 : 0;
    },

    change_equipment_location: function (equipment_location, location_id, silent) {
        if (!silent) {
            this.set('value', []);
        }
        this.location_id = location_id;
        const equipment_in_location = equipment_location.by_location_and_category(
            location_id,
            this.equipment_category_id
        );
        this._location_has_equipment = equipment_in_location.length > 0;
        const equipment_location_ids = equipment_in_location.map((equipment) => {
            return equipment.get('current_location').id;
        });
        const setEquipmentCollectionCallback = () => {
            this.temperature_collection.setEquipmentCollection(equipment_in_location);
            hideLoadingDialog();
        };
        const link_sensors_to_equipment = () => {
            //Add linked sensors to equipment locations
            equipment_in_location.forEach((equipment) => {
                const linked_sensor = this.sensor_collection.find((sensor) => {
                    if (!sensor.get('equipment_location')) {
                        return false;
                    }
                    return sensor.get('equipment_location').id === equipment.get('current_location').id;
                });

                if (linked_sensor) {
                    equipment.set({ sensor: linked_sensor });
                }
            });
            setEquipmentCollectionCallback();
        };
        this.sensor_collection.get_sensors_with_equipment_locations(
            equipment_location_ids,
            link_sensors_to_equipment,
            setEquipmentCollectionCallback
        );
    },

    get_submission_value: function () {
        const equipment_temperature_values = this.get('value');
        if (!equipment_temperature_values || equipment_temperature_values.length === 0) {
            return [];
        }

        // filter out empty values for submission
        return this._clean_up_values(equipment_temperature_values);
    },

    _clean_up_follow_up_actions: function (follow_up_actions_raw) {
        return follow_up_actions_raw.map(({ id, title, value }) => ({ id, title, value }));
    },

    _clean_up_values: function (equipment_temperature_values) {
        return equipment_temperature_values
            .filter((item) => isFilledTemperatureValue(item.value))
            .map((item) => {
                const {
                    id,
                    model_name,
                    required,
                    value,
                    acceptance_value,
                    follow_up_actions: follow_up_actions_raw,
                    reading_method
                } = item;
                const follow_up_actions = follow_up_actions_raw
                    ? this._clean_up_follow_up_actions(follow_up_actions_raw)
                    : null;

                return { id, model_name, required, value, acceptance_value, follow_up_actions, reading_method };
            });
    },

    has_all_values: function () {
        const empty_equipment_temps = this.temperature_collection.filter(
            (equipment_temp) => !isFilledTemperatureValue(equipment_temp.get('value'))
        );

        return this.temperature_collection.length > 0 && empty_equipment_temps.length === 0;
    },

    validation: {
        value: {
            fn: function (val, attr, computed) {
                // Set as valid when this field is required but there're no equipment in the current location
                if (this.get('required')) {
                    if (this.location_id && !this._location_has_equipment) {
                        return;
                    }
                    if (this.get('value').length === 0) {
                        return _t('mobile_message_error_invalid_ingredient_temperature');
                    }
                }
                if (!this.temperature_collection.is_valid()) {
                    return _t('mobile_message_error_invalid_ingredient_temperature');
                }
            }
        }
    },

    get_temperature_field_child_models: function () {
        const fields = [];
        const {
            temperature_collection: { models: temperature_models }
        } = this;

        if (temperature_models) {
            const temperature_fields = temperature_models.reduce(
                (child_models, model) => {
                    if (model.get('follow_up_actions')) {
                        const follow_up_action_models = model.get('follow_up_actions').models;
                        child_models.push(...follow_up_action_models);
                        return child_models;
                    }
                    return child_models;
                },
                [...temperature_models]
            );
            fields.push(...temperature_fields);
        }

        return fields;
    },

    // don't copy the value for media fields on autofill copy
    get_attributes_for_autofill: function () {
        return {};
    }
});

export const IngredientTemperature = MobileTemperatureField.extend({
    defaults: {
        id: null,
        acceptance_criteria: {},
        type: 'ingredient_temperature',
        value: null,
        acceptance_value: null,
        follow_up_actions: null,
        reading_method: null
    },

    submodels: {
        follow_up_actions: FollowUpActionFields,
        acceptance_criteria: AcceptanceCriteriaCollection
    },

    _pass_range_instructions: '',
    _follow_up_actions_num_errors: 0,

    initialize: function (attrs, options) {
        options = options || {};
        $.extend(this, options);

        this._pass_range_instructions = this._get_pass_range_instructions();

        IngredientTemperature.__super__.initialize.call(this, attrs, options);

        this.on('change:acceptance_value', () => {
            this._set_follow_up_actions(this.matched_criteria);
        });
    },

    validation: {
        value: {
            fn: function (value, attr, computed) {
                if (isNaN(value) && value !== 'na') {
                    return _t('mobile_message_error_invalid_ingredient_temperature');
                }
            },
            required: function () {
                return this.get('required');
            }
        }
    },

    _map_pass_range_operator_with_instructions: function (instructions, range) {
        const range_metadata = range.get('metadata');
        if (range_metadata && range_metadata.get('acceptance_value') === AcceptanceValue.PASS) {
            const operator_id = range.get('condition').get('operator_id');
            const condition_value = range.get('condition').get('condition_value');
            const instruction_params = get_instruction_params(
                condition_value,
                $f.active_user.get('temperature_unit') ?? $f.active_company.get('temperature_unit')
            );

            instructions.push(_t(CONDITION_OPERATORS_AND_INSTRUCTIONS[operator_id], instruction_params));
        }

        return instructions;
    },

    _get_pass_range_instructions: function () {
        const pass_ranges_instructions = this.get('acceptance_criteria').reduce(
            this._map_pass_range_operator_with_instructions,
            []
        );

        return pass_ranges_instructions.join(_t('mobile_field_temperature_operators_separator'));
    },

    _reset_acceptance_value: function (use_silent_update) {
        this.set(
            {
                acceptance_value: null,
                follow_up_actions: null
            },
            { silent: use_silent_update }
        );
    },

    get_follow_up_actions_num_errors: function () {
        if (!this.get('follow_up_actions') || this.get('follow_up_actions').is_valid()) {
            return 0;
        }

        return this.get('follow_up_actions').get_num_errors();
    },

    /**
     * If value is null or empty, avoid evaluating with acceptance_criteria,
     * else get matched criteria to calculate acceptance value & follow up actions if apply
     * @param (boolean) - default: false - should we set acceptance_criteria with { silent: true };
     */
    set_acceptance_value: function (use_silent_update) {
        use_silent_update = use_silent_update === undefined ? false : use_silent_update;

        const value = this.get('value');
        if (value === null || value === '' || value === NA) {
            this._reset_acceptance_value(use_silent_update);
            return;
        }

        const acceptance_criteria = this.get('acceptance_criteria');
        if (!acceptance_criteria) {
            return;
        }

        this.matched_criteria = acceptance_criteria.get_matched_criteria(value);
        if (!this.matched_criteria) {
            return;
        }

        const acceptance_value = this.matched_criteria.get('metadata').get('acceptance_value');
        this.set('acceptance_value', acceptance_value, { silent: use_silent_update });
        const prev_value = this.previous('value');
        const prev_acceptance_value = this.previous('acceptance_value');
        if (prev_value !== this.get('value') || prev_acceptance_value !== this.get('acceptance_value')) {
            playAcceptanceValueSound(this.get('reading_method'), this.get('acceptance_value'));
        }
    },

    _set_follow_up_actions: function (matched_criteria) {
        if (this.get('acceptance_value') === AcceptanceValue.FAIL) {
            this.set('follow_up_actions', matched_criteria.get('follow_up_actions'));
            const followUpActions = this.get('follow_up_actions');

            if (followUpActions) {
                this.listenTo(followUpActions, 'change', () => {
                    this.trigger('follow_up_actions_on_change', this);
                });
            }
            this.addRetempAttemptField();
            return;
        }

        if (!this.collection.isEmpty()) {
            if (this.is_original_field) {
                this.removeRetempAttempts();
            } else {
                this.removeRetempAttemptsFromCurrentId();
            }
        }
        this.set('follow_up_actions', null);
    },

    /**
     * Get next model with hidden:false within this temperature_collection
     * If temperature_collection is exhausted, check the next field in overall collection
     * and the underlying temperature_collection
     *
     * @returns: {BaseField || null}
     */
    get_next_visible_temp: function () {
        const base_type = this.get('type');
        const current_temp_idx = this.collection.indexOf(this);
        const next_temp_idx = current_temp_idx + 1;
        const collection_length = this.collection.length;

        // check current collection, return next field if it exists
        if (next_temp_idx < collection_length && current_temp_idx !== -1) {
            return this.collection.at(next_temp_idx);
        }

        // get form collection
        const form_collection = this.collection.form_collection;
        const form_collection_order = form_collection.get_order();
        const current_idx = form_collection_order.indexOf(this.collection.parent_field_id);

        // if this is the last field on the form stop here
        if (current_idx >= form_collection.length || current_idx === -1) {
            return null;
        }

        // loop through remaining field on form (in order) evaluating only visible fields
        for (let idx = current_idx + 1; idx < form_collection_order.length; idx++) {
            const model = form_collection.get(form_collection_order[idx]);
            // skip any hidden models
            if (model.get('hidden') === false) {
                // return model only if it matches the same type as the field just updated
                if (model.temperature_collection && model.get('type') === base_type) {
                    return model.temperature_collection.at(0);
                } else {
                    return null;
                }
            }
        }

        return null;
    }
});

export const IngredientTemperatures = BaseZenputCollection.extend({
    model: IngredientTemperature,
    ingredient_temperature_category_id: '',
    required: false,
    allow_na: false,
    random_check: false,
    check_all: true,
    ingredients_catalog: null,
    all_ingredients_shown: false,
    parent_field_id: null,
    _invalid_temperatures: 0,

    initialize: function (attrs, options) {
        options = options || {};
        $.extend(this, options);

        IngredientTemperatures.__super__.initialize.call(this, attrs, options);
    },

    /**
     * Checks each model if they're valid.
     * @return {Boolean} True if all models are valid, false if any aren't.
     */
    is_valid: function () {
        if (this.required && this.length === 0) {
            return false;
        }

        const required_validations = this.map((ingredient_temp) => {
            // isValid function returns response after some delay, therefore checking
            // manually if this is required and there's a value.
            if (ingredient_temp.get('required') && !ingredient_temp.get('value')) {
                return false;
            }

            return true;
        });

        return required_validations.filter((is_valid) => is_valid === false).length === 0;
    },

    get_num_invalid: function () {
        return this.reduce((errors_counter, ingredient_temp) => {
            if (ingredient_temp.isValid(true) === false) {
                errors_counter++;
            }
            errors_counter += ingredient_temp.get_follow_up_actions_num_errors();
            return errors_counter;
        }, 0);
    },

    getAllowedRetemps: function () {
        const ingredientFormModel = this.form_collection.findWhere({ id: this.parent_field_id });
        return ingredientFormModel.get_metadata_attribute('allowed_retemps');
    },

    add_ingredient_temperature: function (ingredient_temperature_data) {
        const saved_ingredient_temperature = new IngredientTemperature(ingredient_temperature_data);
        const allowedRetemps = this.getAllowedRetemps();

        if (this.random_check) {
            saved_ingredient_temperature.on('change:value', (_, value) => {
                if (value === NA && this.should_get_new_random_ingredient()) {
                    this.get_random_ingredient();
                }
            });

            saved_ingredient_temperature.on('change:acceptance_value', (_, acceptance_value) => {
                if (
                    acceptance_value === AcceptanceValue.FAIL &&
                    this.should_show_all_ingredients() &&
                    !allowedRetemps
                ) {
                    this.get_all_ingredients();
                    this.mark_ingredients_as_required();
                }
            });

            this.on('change:acceptance_value', () => {
                const lastRetemp = this.findWhere({ retemp_attempt_number: allowedRetemps });

                if (lastRetemp?.get('acceptance_value') === AcceptanceValue.FAIL && this.check_all) {
                    this.get_all_ingredients();
                    this.mark_ingredients_as_required();
                }
            });
        }

        this.add(saved_ingredient_temperature);
    },

    set_submitted_values: function (submitted_values) {
        const values = submitted_values.map((value) => ({ allow_na: this.allow_na, ...value }));
        this.add(values);
    },

    set_saved_values: function (saved_values) {
        saved_values.forEach((value) => this.add_ingredient_temperature({ allow_na: this.allow_na, ...value }));
    },

    _location_has_random_disabled: function (sampleIngredient) {
        if (!sampleIngredient) {
            return false;
        }

        const {
            location: { random_ingredient_temperature_disabled }
        } = sampleIngredient.get('current_location');
        return random_ingredient_temperature_disabled;
    },

    set_ingredients_catalog: function (new_ingredients_catalog, silent) {
        this.ingredients_catalog = new_ingredients_catalog;

        if (silent) {
            return;
        }

        this.reset();
        this.all_ingredients_shown = false;

        if (!this.random_check || this._location_has_random_disabled(new_ingredients_catalog.first())) {
            this.get_all_ingredients();
        } else {
            this.get_random_ingredient();
        }
    },

    get_all_ingredients: function () {
        if (this.ingredients_catalog === null) {
            return;
        }

        const ingredients = this.ingredients_catalog.map((ingredient) => ({
            id: ingredient.id,
            name: ingredient.get('name'),
            required: this.required,
            value: '',
            allow_na: this.allow_na,
            acceptance_criteria: ingredient.get('current_location').acceptance_criteria,
            acceptance_value: null
        }));
        this.add(ingredients);
    },

    mark_ingredients_as_required: function () {
        this.each(function (item) {
            item.set('required', true);
        });
    },

    search_unexisting_random_ingredient: function () {
        if (!this.ingredients_catalog || this.ingredients_catalog.length === 0) {
            return;
        }

        const random_index = Math.floor(Math.random() * this.ingredients_catalog.length);
        const random_ingredient = this.ingredients_catalog.at(random_index);

        if (this.get(random_ingredient.id)) {
            if (this.length === this.ingredients_catalog.length) {
                return;
            }
            return this.search_unexisting_random_ingredient();
        }

        return random_ingredient;
    },

    get_random_ingredient: function () {
        const random_ingredient = this.search_unexisting_random_ingredient();
        if (!random_ingredient) {
            return;
        }

        this.add_ingredient_temperature({
            id: random_ingredient.id,
            name: random_ingredient.get('name'),
            required: this.required,
            value: '',
            allow_na: this.allow_na,
            acceptance_criteria: random_ingredient.get('current_location').acceptance_criteria
        });
    },

    should_get_new_random_ingredient: function () {
        const not_na_values = this.filter((ingredient_temperature) => ingredient_temperature.get('value') !== NA);
        return not_na_values.length === 0;
    },

    should_show_all_ingredients: function () {
        if (!this.check_all || this.all_ingredients_shown) {
            return false;
        }

        this.all_ingredients_shown = true;
        return true;
    },

    search_saved_value: function (saved_values, id) {
        const filtered_values = saved_values.filter((saved_value) => saved_value.id === id);

        return filtered_values.length ? filtered_values[0] : null;
    }
});

/**
 * A single Ingredient Catalog Item for Ingredient Temperature Field
 */
export const MobileIngredient = Ingredient.extend({
    defaults: {
        id: null, // Id
        name: ''
    },

    _parse_follow_up_actions: function (follow_up_actions) {
        return follow_up_actions.map(({ id, is_required, title }) => ({ id, required: is_required, title }));
    },

    // Parse ingredient acceptance criteria into form acceptance criteria structure
    _ingredient_ac_to_form_ac: function (conditions) {
        return conditions.map(({ condition, value, acceptance_value, follow_up_actions }) => ({
            condition: {
                condition_value: value,
                operator_id: ACCEPTANCE_CRITERIA_OPERATORS[condition]
            },
            metadata: { acceptance_value },
            follow_up_actions: this._parse_follow_up_actions(follow_up_actions)
        }));
    },

    _parse_ingredient_locations: function (ingredient_server_data) {
        const { ingredient_location, ...ingredient } = ingredient_server_data;

        if (!ingredient_location || ingredient_location.length === 0) {
            return { ...ingredient };
        }

        // Extract ingredient_temperature_profiles and calculate acceptance criteria
        const parsed_locations = ingredient_location.map((location) => {
            const { ingredient_temperature_profiles, ...location_without_temp_profile } = location;
            if (ingredient_temperature_profiles.length === 0) {
                return location_without_temp_profile;
            }

            const { conditions } = ingredient_temperature_profiles[0];
            const acceptance_criteria = this._ingredient_ac_to_form_ac(conditions);
            return { acceptance_criteria, ...location_without_temp_profile };
        });

        return { locations: parsed_locations, ...ingredient };
    },

    parse: function (received_data) {
        const ingredient_data = received_data.data || received_data;

        return this._parse_ingredient_locations(ingredient_data);
    }
});

/**
 * Ingredient Count for an account
 */

export const IngredientsMetadata = BaseZenputModel.extend({
    defaults: {
        count: 0,
        location_id: []
    },

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        IngredientsMetadata.__super__.initialize.apply(this, arguments);
    },

    url: function () {
        const endpoint = '/api/v3/activities/ingredients/?meta_only=true';

        if (this.location_id) {
            return `${endpoint}&location_ids=${this.location_id}&temp_profile_type=${TEMPERATURE_PROFILE_TYPE_HOLDING}`;
        }

        return endpoint;
    },

    parse: function (response) {
        return response.meta;
    }
});

/**
 * Ingredient Catalog Items for Ingredient Temperature Field
 */
export const MobileIngredients = Ingredients.extend(
    {
        model: MobileIngredient,
        paginator: null,
        cache_enabled: true,
        cache_key: 'ingredients',
        cache_fetch_limit: 100,
        location_id: null,
        ingredients_count: 0,
        cache_disable_url_params: ['uncached_location'],
        cache_merge_on_fetch: true,

        initialize: function (models, options) {
            options = options || {};
            $.extend(this, options);

            if (!this.paginator) {
                const { location_id, uncached_location } = options;
                this.paginator = new Paginator({ location_id, uncached_location });
            }

            MobileIngredients.__super__.initialize.apply(this, arguments);
        },

        url: function () {
            const endpoint = '/api/v3/activities/ingredients/?';

            if (this.location_id) {
                return `${endpoint}&location_ids=${this.location_id}&temp_profile_type=${TEMPERATURE_PROFILE_TYPE_HOLDING}&limit=${this.ingredients_count}`;
            }

            return `${endpoint}&limit=${this.ingredients_count}`;
        },

        _get_ingredients_count: function (success, error) {
            const ingredients_metadata = new IngredientsMetadata(null, { location_id: this.location_id });
            return ingredients_metadata.fetch({
                success: (model) => {
                    this.ingredients_count = model.get('count');
                    this.paginator.set('limit', this.ingredients_count);
                    this.set('cache_fetch_limit', this.ingredients_count); // Setting cache_fetch_limit to not skip cache
                    success();
                },
                error
            });
        },

        sync: function (method, model, options) {
            options = options || {};

            const send_request_cb = (locale) => {
                locale = locale || $f.mobile.localization.DEFAULT_LOCALE;
                locale = locale.replace('_', '-');
                options.headers = {
                    'Accept-Language': locale,
                    ...options.headers
                };

                return Backbone.sync(method, model, options);
            };

            $f.mobile.localization.get_locale(send_request_cb);
        },

        fetch: function (options = {}) {
            const args = arguments;
            const { error = () => {} } = options;
            this._get_ingredients_count(() => MobileIngredients.__super__.fetch.apply(this, args), error);
        },

        by_location_and_category: function (location_id, category_id) {
            const filter_reducer = (filtered_ingredient_by_locations, ingredient) => {
                if (!ingredient.get('locations')) {
                    return filtered_ingredient_by_locations;
                }

                const matched_location = ingredient
                    .get('locations')
                    .find(({ location }) => location.id === location_id);

                if (matched_location) {
                    if (category_id && !ingredient.get('categories').some((category) => category.id === category_id)) {
                        return filtered_ingredient_by_locations;
                    }

                    ingredient.set('current_location', matched_location);
                    filtered_ingredient_by_locations.push(ingredient);
                }

                return filtered_ingredient_by_locations;
            };

            const filtered_ingredients = this.reduce(filter_reducer, []);
            return new MobileIngredients(filtered_ingredients);
        }
    },
    {
        fetch_ingredients_for_location: function (location_id, error_callback) {
            return new Promise((resolve, reject) => {
                if (location_id === null) {
                    return resolve(new MobileIngredients([]));
                }
                // We always fetch the ingredient for the location, even if we have a cache
                const uncached_ingredients_location = new MobileIngredients([], {
                    location_id,
                    uncached_location: true
                });
                uncached_ingredients_location.fetch({
                    success: resolve,
                    error: reject
                });
            }).then(
                (resolvedEquipment) => resolvedEquipment,
                () => {
                    error_callback();
                    // On failure we attempt to fetch the ingredients from the cache
                    return new Promise((resolve, reject) => {
                        $f.mobile.cache.get(
                            'ingredients',
                            (ingredientCollection) => {
                                resolve(new MobileIngredients(ingredientCollection));
                            },
                            reject
                        );
                    });
                }
            );
        }
    }
);

export const EquipmentMetadata = BaseZenputModel.extend({
    defaults: {
        count: 0
    },

    url: function () {
        return '/api/v2/equipment/equipment/?meta_only=true';
    },

    parse: function (response) {
        return response.meta;
    }
});

export const EquipmentModel = BaseZenputModel.extend({
    defaults: {
        id: null,
        model_name: null,
        unique_id: null
    },

    idAttribute: 'unique_id',

    url: function () {
        return `/api/v2/equipment/equipment/${this.id}/`;
    },

    _parse_follow_up_actions: function (follow_up_actions) {
        return follow_up_actions.map(({ id, is_required, title }) => ({ id, required: is_required, title }));
    },

    _equipment_ac_to_form_ac: function (conditions) {
        return conditions.map(({ condition, value, acceptance_value, follow_up_actions }) => ({
            condition: {
                condition_value: value,
                operator_id: ACCEPTANCE_CRITERIA_OPERATORS[condition]
            },
            metadata: { acceptance_value },
            follow_up_actions: this._parse_follow_up_actions(follow_up_actions)
        }));
    },

    _parse_equipment_locations: function (equipment_server_data) {
        const { equipment_location, ...equipment } = equipment_server_data;

        if (!equipment_location || equipment_location.length === 0) {
            return { ...equipment };
        }

        // Extract equipment_temperature_profile and calculate acceptance criteria
        const parsed_locations = equipment_location.map((location) => {
            const { equipment_temperature_profiles, ...location_without_temp_profile } = location;
            if (equipment_temperature_profiles.length === 0) {
                return location_without_temp_profile;
            }
            const { conditions } = equipment_temperature_profiles[0];

            const acceptance_criteria = this._equipment_ac_to_form_ac(conditions);
            return { acceptance_criteria, ...location_without_temp_profile };
        });

        return { locations: parsed_locations, ...equipment };
    },

    parse: function (received_data) {
        const equipment_data = received_data.data || received_data;
        return this._parse_equipment_locations(equipment_data);
    }
});

export const EquipmentCollection = BaseZenputCollection.extend(
    {
        model: EquipmentModel,
        location_id: null,
        equipment_count: 0,
        cache_fetch_limit: 100,
        paginator: null,
        typeahead_search_attrs: ['model_name'],
        cache_enabled: true,
        cache_key: 'equipment',
        cache_disable_url_params: ['uncached_location'],
        cache_merge_on_fetch: true,

        initialize: function (models, options) {
            options = options || {};
            $.extend(this, options);

            if (!this.paginator) {
                const { location_id, uncached_location } = options;
                this.paginator = new Paginator({ location_id, uncached_location });
            }

            EquipmentCollection.__super__.initialize.apply(this, arguments);
        },

        url: function () {
            const endpoint = '/api/v2/equipment/equipment/';

            if (this.location_id) {
                return `${endpoint}?location_id=${this.location_id}&limit=${this.equipment_count}`;
            }

            return `${endpoint}?limit=${this.equipment_count}`;
        },

        _get_equipment_count: function (success, error) {
            const equipment_metadata = new EquipmentMetadata();
            return equipment_metadata.fetch({
                success: (model) => {
                    this.equipment_count = model.get('count');
                    this.paginator.set('limit', this.equipment_count);
                    this.set('cache_fetch_limit', this.ingredients_count); // Setting cache_fetch_limit to not skip cache
                    success();
                },
                error
            });
        },

        fetch: function (options = {}) {
            const args = arguments;
            const { error = () => {} } = options;
            this._get_equipment_count(() => EquipmentCollection.__super__.fetch.apply(this, args), error);
        },

        by_location_and_category: function (location_id, category_id) {
            const reducer_function = (filtered_equipment, equipment) => {
                const equipment_locations = equipment.get('locations');
                if (!equipment_locations) {
                    return filtered_equipment;
                }
                const current_locations = equipment_locations.filter(({ location }) => location.id === location_id);

                current_locations.forEach((current_location, i) => {
                    const modelDescription = [
                        equipment.get('model_name'),
                        current_location.identifier,
                        current_location.location_zone
                    ]
                        .filter((x) => Boolean(x))
                        .join(' - ');

                    if (category_id && !equipment.get('categories').some((category) => category.id === category_id)) {
                        return;
                    }
                    const json_equipment = {
                        ...equipment.toJSON(),
                        current_location,
                        unique_id: current_locations.length > 1 ? `${equipment.get('id')}-${i}` : equipment.get('id'),
                        model_name: modelDescription
                    };
                    filtered_equipment.push(json_equipment);
                });
                return filtered_equipment;
            };

            const filtered_equipment_by_location = this.reduce(reducer_function, []);
            return new EquipmentCollection(filtered_equipment_by_location);
        }
    },
    {
        fetch_equipment_for_location: function (location_id, error_callback) {
            return new Promise((resolve, reject) => {
                if (location_id === null) {
                    return resolve(new EquipmentCollection([]));
                }
                // We always fetch the equipment for the location, even if we have a cache
                const uncached_equipment_location = new EquipmentCollection([], {
                    location_id,
                    uncached_location: true
                });
                uncached_equipment_location.fetch({
                    success: resolve,
                    error: reject
                });
            }).then(
                (resolvedEquipment) => resolvedEquipment,
                () => {
                    error_callback();
                    // On failure we attempt to fetch the equipment from the cache
                    return new Promise((resolve, reject) => {
                        return $f.mobile.cache.get(
                            'equipment',
                            (equipmentFromCache) => {
                                resolve(new EquipmentCollection(equipmentFromCache));
                            },
                            reject
                        );
                    });
                }
            );
        }
    }
);

export const EquipmentLocation = BaseZenputModel.extend({
    defaults: {
        id: null,
        location_zone: null,
        identifier: null,
        equipment: null
    },

    idAttribute: 'id',

    submodels: {
        equipment: EquipmentModel
    }
});

export const EquipmentLocationCollection = BaseZenputCollection.extend({
    paginator: null,
    model: EquipmentLocation,
    locationId: null,
    excludeEquipmentWithSensorLinked: false,
    typeahead_search_attrs: ['searchText'],
    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        if (!this.paginator) {
            this.paginator = new Paginator();
        }
    },

    url: function () {
        let url = '/api/v2/equipment/location/';

        if (this.locationId) url = `${url}?location_id=${this.locationId}`;

        if (this.paginator.get('search_query')) url = `${url}&search=${this.paginator.get('search_query')}`;

        if (this.excludeEquipmentWithSensorLinked)
            url = `${url}&exclude_equipment_with_sensor_linked=${this.excludeEquipmentWithSensorLinked}`;

        return url;
    }
});

// Used for MobileEquipmentTemperatureField
export const EquipmentTemperature = MobileTemperatureField.extend({
    defaults: {
        id: null,
        acceptance_criteria: {},
        type: 'equipment_temperature',
        value: null,
        acceptance_value: null,
        follow_up_actions: null,
        sensor: null,
        reading_method: null
    },

    submodels: {
        follow_up_actions: FollowUpActionFields,
        acceptance_criteria: AcceptanceCriteriaCollection,
        sensor: SensorTemperature
    },

    _pass_range_instructions: '',
    _follow_up_actions_num_errors: 0,

    initialize: function (attrs, options) {
        options = options || {};
        $.extend(this, options);
        this._pass_range_instructions = this._get_pass_range_instructions();

        EquipmentTemperature.__super__.initialize.call(this, attrs, options);

        this.on('change:acceptance_value', () => this._set_follow_up_actions(this.matched_criteria));
    },

    validation: {
        value: {
            fn: function (value, attr, computed) {
                if (isNaN(value) && value !== 'na') {
                    return _t('mobile_message_error_invalid_equipment_temperature');
                }
            },
            required: function () {
                return this.get('required');
            }
        }
    },

    _map_pass_range_operator_with_instructions: function (instructions, range) {
        const range_metadata = range.get('metadata');
        if (range_metadata && range_metadata.get('acceptance_value') === AcceptanceValue.PASS) {
            const operator_id = range.get('condition').get('operator_id');
            const condition_value = range.get('condition').get('condition_value');
            const instruction_params = get_instruction_params(
                condition_value,
                $f.active_user.get('temperature_unit') ?? $f.active_company.get('temperature_unit')
            );
            instructions.push(_t(CONDITION_OPERATORS_AND_INSTRUCTIONS[operator_id], instruction_params));
        }
        return instructions;
    },

    _get_pass_range_instructions: function () {
        const pass_ranges_instructions = this.get('acceptance_criteria').reduce(
            this._map_pass_range_operator_with_instructions,
            []
        );
        return pass_ranges_instructions.join(_t('mobile_field_temperature_operators_separator'));
    },

    _reset_acceptance_value: function (use_silent_update) {
        this.set(
            {
                acceptance_value: null,
                follow_up_actions: null
            },
            { silent: use_silent_update }
        );
    },

    _set_follow_up_actions: function (matched_criteria) {
        if (this.get('acceptance_value') === AcceptanceValue.FAIL) {
            this.set('follow_up_actions', matched_criteria.get('follow_up_actions'));
            const followUpActions = this.get('follow_up_actions');
            if (followUpActions) {
                this.listenTo(followUpActions, 'change', () => {
                    this.trigger('follow_up_actions_on_change', this);
                });
            }
            this.addRetempAttemptField();
            return;
        }

        if (!this.collection.isEmpty()) {
            if (this.is_original_field) {
                this.removeRetempAttempts();
            } else {
                this.removeRetempAttemptsFromCurrentId();
            }
        }

        this.set('follow_up_actions', null);
    },

    get_follow_up_actions_num_errors: function () {
        if (!this.get('follow_up_actions') || this.get('follow_up_actions').is_valid()) {
            return 0;
        }

        return this.get('follow_up_actions').get_num_errors();
    },

    /**
     * If value is null or empty, avoid evaluating with acceptance_criteria,
     * else get matched criteria to calculate acceptance value & follow up actions if apply
     * @param (boolean) - default: false - should we set acceptance_criteria with { silent: true };
     */
    set_acceptance_value: function (use_silent_update) {
        use_silent_update = use_silent_update === undefined ? false : use_silent_update;

        const value = this.get('value');
        if (value === null || value === '' || value === NA) {
            this._reset_acceptance_value(use_silent_update);
            return;
        }

        // If Acceptance Criteria is missing, avoid evaluation
        const acceptance_criteria = this.get('acceptance_criteria');
        if (!acceptance_criteria) {
            return;
        }

        this.matched_criteria = acceptance_criteria.get_matched_criteria(value);
        if (!this.matched_criteria) {
            return;
        }

        const acceptance_value = this.matched_criteria.get('metadata').get('acceptance_value');
        this.set('acceptance_value', acceptance_value, { silent: use_silent_update });
        const prev_value = this.previous('value');
        const prev_acceptance_value = this.previous('acceptance_value');
        if (prev_value !== this.get('value') || prev_acceptance_value !== this.get('acceptance_value')) {
            playAcceptanceValueSound(this.get('reading_method'), this.get('acceptance_value'));
        }
    },

    /**
     * Get next model with hidden:false within this temperature_collection
     * If temperature_collection is exhausted, check the next field in overall collection
     * and the underlying temperature_collection
     *
     * @returns: {BaseField || null}
     */
    get_next_visible_temp: function () {
        const base_type = this.get('type');
        const current_temp_idx = this.collection.indexOf(this);
        const next_temp_idx = current_temp_idx + 1;
        const collection_length = this.collection.length;

        // check current collection, return next field if it exists
        if (next_temp_idx < collection_length && current_temp_idx !== -1) {
            return this.collection.at(next_temp_idx);
        }

        // get form collection
        const form_collection = this.collection.form_collection;
        const form_collection_order = form_collection.get_order();
        const current_idx = form_collection_order.indexOf(this.collection.parent_field_id);

        // if this is the last field on the form stop here
        if (current_idx >= form_collection.length || current_idx === -1) {
            return null;
        }

        // loop through remaining field on form (in order) evaluating only visible fields
        for (let idx = current_idx + 1; idx < form_collection_order.length; idx++) {
            const model = form_collection.get(form_collection_order[idx]);
            // skip any hidden models
            if (model.get('hidden') === false) {
                // return model only if it matches the same type as the field just updated
                if (model.temperature_collection && model.get('type') === base_type) {
                    return model.temperature_collection.at(0);
                } else {
                    return null;
                }
            }
        }

        return null;
    }
});

export const EquipmentTemperatures = BaseZenputCollection.extend({
    model: EquipmentTemperature,
    equipment_category_id: '',
    equipment_collection: null,
    required: false,
    allow_na: false,
    parent_field_id: null,
    _invalid_temperatures: 0,

    initialize: function (attrs, options) {
        options = options || {};
        $.extend(this, options);

        EquipmentTemperatures.__super__.initialize.call(this, attrs, options);
    },

    /**
     * Checks each model if they're valid.
     * @return {Boolean} True if all models are valid, false if any aren't.
     */
    is_valid: function () {
        if (this.required && this.length === 0) {
            return false;
        }

        const required_validations = this.map((equipment_temp) => {
            // isValid function returns response after some delay, therefore checking
            // manually if this is required and there's a value.
            if (equipment_temp.get('required') && !equipment_temp.get('value')) {
                return false;
            }

            return true;
        });

        return required_validations.filter((is_valid) => is_valid === false).length === 0;
    },

    get_num_invalid: function () {
        return this.reduce((errors_counter, ingredient_temp) => {
            if (ingredient_temp.isValid(true) === false) {
                errors_counter++;
            }
            errors_counter += ingredient_temp.get_follow_up_actions_num_errors();
            return errors_counter;
        }, 0);
    },

    set_submitted_values: function (submitted_values) {
        const values = submitted_values.map((value) => ({ allow_na: this.allow_na, ...value }));
        this.add(values);
    },

    // This method needs to exist because the view is shared with IngredientTemperatures
    // and in that model it does different things than set_submitted_values
    set_saved_values: function (saved_values) {
        this.set_submitted_values(saved_values);
    },

    // Used in the view to create a collection of temperatures depending on the equipment
    setEquipmentCollection: function (newEquipmentCollection) {
        this.equipment_collection = newEquipmentCollection;

        const equipmentTemperatureData = this.equipment_collection.map((equipment) => {
            const equipmentTemperature = this.findWhere({ model_name: equipment.get('model_name') });
            return {
                id: equipment.get('unique_id'),
                model_name: equipment.get('model_name'),
                value: equipmentTemperature ? equipmentTemperature.get('value') : '',
                required: this.required,
                allow_na: this.allow_na,
                acceptance_criteria: equipment.get('current_location').acceptance_criteria,
                sensor: equipment.get('sensor'),
                acceptance_value: null
            };
        });

        this.reset();
        this.add(equipmentTemperatureData);
    }
});
